import React, { FC, ReactElement } from 'react';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
// Components
import Space, { Type } from '../layout/space';
import Text from '../text';
import TextCenterButton from '../buttons/textCenterButton';
import SvgIcon from '../icons/SvgIcon';
import WickedImage from '../events/wicked/WickedImage';
// Resources
import { IconAxel, ImageEvent } from '../../resources/images';
import { stringsRes } from '../../resources/strings';
import useGlobalStyles from '../hook/useGlobalStyles';

interface PropsFromModal {
    onRequestClose?: () => void;
}

interface WickedProps extends PropsFromModal {}

const Wicked: FC<WickedProps> = (props): ReactElement => {
    const { onRequestClose } = props;
    const classes = useStyle();
    const globalClasses = useGlobalStyles();

    const onClickConfirm = (): void => {
        onRequestClose();
    };

    const items = [
        { title: stringsRes.wicked.bulletGreenTitle, description: stringsRes.wicked.bulletGreenDescription },
        { title: stringsRes.wicked.bulletCoverTitle, description: stringsRes.wicked.bulletCoverDescription },
    ];

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <div className={classes.logoContainer}>
                    <WickedImage type={ImageEvent.WickedModal} />
                </div>
                <Text.Description14 text={stringsRes.wicked.description} />
            </div>

            <Space type={Type.vertical} value={3} />

            <div className={classes.itemsContainer}>
                {items.map(({ title, description }) => (
                    <div className={classes.itemContainer} key={title}>
                        <SvgIcon icon={IconAxel.Check} className={classes.icon} overwritePathStroke />
                        <div>
                            <Text.Description14 text={title} />
                            <Text.Description14 text={description} customColor="grey" />
                        </div>
                    </div>
                ))}
            </div>

            <div className={classes.buttonContainer}>
                <TextCenterButton
                    id="validate"
                    title={stringsRes.wicked.validate}
                    onClick={onClickConfirm}
                    className={globalClasses.wickedGradient}
                />
            </div>
        </div>
    );
};

const useStyle = makeStyles((theme: Theme) =>
    createStyles({
        container: {},
        content: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        },
        titleContainer: {
            display: 'flex',
            placeItems: 'center',
            justifyContent: 'space-between',
        },
        title: {
            fontSize: 28,
        },
        logoContainer: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 10,
            padding: '24px 0px',
        },
        buttonContainer: {
            display: 'flex',
            marginTop: theme.spacing(3),
            flexDirection: 'column',
            justifyContent: 'center',
        },
        closeIcon: {
            alignSelf: 'baseline',
            cursor: 'pointer',
        },
        itemsContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
        },
        itemContainer: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 16,
            borderRadius: 8,
            padding: 16,
            border: `1px solid ${theme.palette.colors.background.menuSeparator}`,
        },
        icon: {
            backgroundColor: '#2D5A44',
            width: 26,
            height: 26,
            borderRadius: 100,
            padding: 6,
            flexShrink: 0,
        },
    }),
);

export default Wicked;
